/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import styled from '@emotion/styled'
import { graphql } from 'gatsby'
import Img from 'gatsby-image/withIEPolyfill'
import Layout from '../components/Layout'
import Container from '../components/Container'
import ContainerHero from '../components/ContainerHero'
import ContainerContent from '../components/ContainerContent'
import PageTitle from '../components/PageTitle'
import SEO from '../components/SEO'
import BackgroundImage from 'gatsby-background-image'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import Newsletter from '../components/NewsletterPopup.js'

const HeroLeft = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  flex-grow: 1;
  width: 100%;
  padding: 30px 15px;
  overflow: hidden;
  @media (min-width: ${props => props.theme.responsive.medium}) {
    border-right: 2px solid #000;
    padding: 30px 60px;
  }
`

const HeroCenter = styled(BackgroundImage)`
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;
  width: 100%;
  max-width: 438px;
  display: flex;
  @media (max-width: ${props => props.theme.responsive.mediumMax}) {
    display: none;
  }
  @media (min-width: ${props => props.theme.responsive.medium}) {
    flex: 0 0 328px;
    width: auto;
  }
  @media (min-width: ${props => props.theme.responsive.large}) {
    flex: 0 0 438px;
  }
`

const Spitzname = styled.div`
  color: ${props => props.theme.colors.red};
`
const Top = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 2px solid #000;
  @media (min-width: ${props => props.theme.responsive.medium}) {
    flex-direction: row;
  }
`
const TopLeft = styled.div`
  font-size: ${props => props.theme.fontSizes[3]}px;
  width: 100%;
  text-transform: uppercase;
  word-break: break-word;
  padding: 30px 15px;
  line-height: 1em;
  @media (min-width: ${props => props.theme.responsive.small}) {
    font-size: ${props => props.theme.fontSizes[3]}px;
  }
  @media (min-width: ${props => props.theme.responsive.medium}) {
    flex: 1 1 50%;
    border-right: 2px solid #000;
    padding: 30px 60px;
    font-size: ${props => props.theme.fontSizes[4]}px;
  }
  @media (min-width: ${props => props.theme.responsive.large}) {
    font-size: ${props => props.theme.fontSizes[6]}px;
  }
`

const Mail = styled.a`
  margin-top: 10px;
  font-size: ${props => props.theme.fontSizes[3]}px;
  font-weight: 400;
  border: 2px solid #000;
  padding: 5px 20px;
  display: inline-block;
  border-radius: 99px;
  opacity: 1 !important;
  &:hover {
    background: #000;
    color: #fff;
  }
  @media (min-width: ${props => props.theme.responsive.medium}) {
    font-size: ${props => props.theme.fontSizes[5]}px;
    padding: 10px 40px;
    margin-top: 30px;
    margin-bottom: 30px;
  }
`

const TopRight = styled.div`
  width: 100%;
  padding: 0 15px 30px;
  @media (min-width: ${props => props.theme.responsive.medium}) {
    flex: 0 0 calc(280px + 330px);
    padding: 30px 60px;
  }
  @media (min-width: ${props => props.theme.responsive.large}) {
    flex: 0 0 calc(280px + 440px);
    padding: 30px 60px;
  }

  .addColumns {
    @media (min-width: ${props => props.theme.responsive.medium}) {
      column-count: 2;
      column-gap: 20px;
      white-space: pre-wrap;
    }
  }
`

const Fact = styled.h2`
  display: inline-block;
  padding: 10px 30px;
  margin-bottom: 20px;
  border: 2px solid ${props => props.theme.colors.red};
  font-size: ${props => props.theme.fontSizes[5]}px;
  @media (max-width: ${props => props.theme.responsive.largeMax}) {
    font-size: ${props => props.theme.fontSizes[4]}px;
  }
  @media (max-width: ${props => props.theme.responsive.mediumMax}) {
    font-size: ${props => props.theme.fontSizes[4]}px;
  }
  @media (max-width: ${props => props.theme.responsive.smallMax}) {
    font-size: ${props => props.theme.fontSizes[3]}px;
  }
`

const FactSmall = styled.div`
  margin-top: 30px;
  @media (min-width: ${props => props.theme.responsive.medium}) {
    width: 50%;
    float: left;
    margin-top: 40px;
  }
  &:nth-of-type(odd) {
    @media (min-width: ${props => props.theme.responsive.medium}) {
      padding-right: 20px;
    }
    h2 {
      border-radius: 999px;
    }
  }
  &:nth-of-type(even) {
    @media (min-width: ${props => props.theme.responsive.medium}) {
      padding-left: 20px;
    }
    h2 {
      border: none !important;
      position: relative;
      &::before {
        content: '';
        width: 100%;
        height: 100%;
        position: absolute;
        left: 0;
        top: 0;
        clip-path: polygon(15% 0%, 85% 0%, 100% 50%, 85% 100%, 15% 100%, 0 50%);
        background-color: ${props => props.theme.colors.background};
        z-index: -1;
      }
      &::after {
        content: '';
        width: calc(100% + 4px);
        height: calc(100% + 4px);
        position: absolute;
        left: -2px;
        top: -2px;
        clip-path: polygon(15% 0%, 85% 0%, 100% 50%, 85% 100%, 15% 100%, 0 50%);
        background-color: ${props => props.theme.colors.red};
        z-index: -2;
      }
    }
  }
`

const Weiteres = styled.div`
  margin-top: 30px;
  @media (min-width: ${props => props.theme.responsive.medium}) {
    margin-top: 40px;
  }
  h3 {
    margin-top: 1rem;
    font-weight: bold;
  }

  ul,
  li {
    list-style: square inside;
  }

  ul li p {
    display: inline-block;
  }
`

const Galerie = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
`

const GalerieImage = styled.div`
  margin-top: 16px;
  position: relative;
  width: calc(50% - 8px);
  padding: 0;

  .gatsby-image-wrapper {
    height: auto;
  }
`

const StyledImg = styled(Img)`
  width: 100%;
`

const TeamTemplate = ({ data }) => {
  const {
    title,
    metaDescription,
    spitzname,
    position,
    email,
    hauptbild,
    einzelbild,
    werdegang,
    skills,
    privates,
    weiteres,
    galerie,
  } = data.contentfulTeam

  return (
    <Layout>
      <Newsletter />
      <SEO
        title={title}
        description={
          metaDescription ? metaDescription.internal.content : werdegang
        }
      />
      <Container>
        <ContainerHero>
          <HeroLeft>
            <PageTitle
              css={css`
                text-transform: uppercase;
              `}
            >
              <div>{title}</div>
              <Spitzname>{spitzname}</Spitzname>
            </PageTitle>
          </HeroLeft>
          <HeroCenter
            Tag="div"
            fluid={einzelbild ? einzelbild.fluid : hauptbild.fluid}
            backgroundColor={`#f2f2f2`}
            title="Fullscreen Background"
            id="fullscreenbg"
            role="img"
            aria-label="Fullscreen Background"
          />
        </ContainerHero>
        <ContainerContent>
          <Top>
            <TopLeft>
              <div>{position}</div>
              <div>
                <Mail href={`mailto:${email}`}>Mail</Mail>
              </div>
              <Galerie>
                {galerie &&
                  galerie.map(node => (
                    <GalerieImage key={node.id}>
                      <StyledImg
                        fluid={node.fluid}
                        backgroundColor={'#eeeeee'}
                        alt={node.title}
                      />
                    </GalerieImage>
                  ))}
              </Galerie>
            </TopLeft>
            <TopRight>
              <Fact>Werdegang</Fact>
              <div className="addColumns">{werdegang.internal.content}</div>
              <div
                css={css`
                  display: inline-block;
                  width: 100%;
                `}
              >
                <FactSmall>
                  <Fact>Aufgaben</Fact>
                  <div>{documentToReactComponents(skills.json)}</div>
                </FactSmall>
                <FactSmall>
                  <Fact>Privates</Fact>
                  <div>{privates.internal.content}</div>
                </FactSmall>
              </div>
              {weiteres && (
                <Weiteres>
                  <Fact>Weiteres</Fact>{' '}
                  <div>{documentToReactComponents(weiteres.json)}</div>
                </Weiteres>
              )}
            </TopRight>
          </Top>
        </ContainerContent>
      </Container>
    </Layout>
  )
}

export const query = graphql`
  query($slug: String!) {
    contentfulTeam(slug: { eq: $slug }) {
      metaDescription {
        internal {
          content
        }
      }
      email
      slug
      spitzname
      weiteres {
        json
      }
      privates {
        internal {
          content
        }
      }
      werdegang {
        internal {
          content
        }
      }
      title
      position
      hauptbild {
        fluid(maxWidth: 468, quality: 90) {
          ...GatsbyContentfulFluid_withWebp
        }
      }
      einzelbild {
        fluid(maxWidth: 468, quality: 90) {
          ...GatsbyContentfulFluid_withWebp
        }
      }
      skills {
        json
      }
      galerie {
        id
        title
        fluid(maxWidth: 558, quality: 90) {
          ...GatsbyContentfulFluid_withWebp
        }
      }
    }
  }
`

export default TeamTemplate
